import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ToastService } from './toast.service';

/**
 * Servicio de autorización para verificar permisos del usuario en el frontend.
 * Los permisos se obtienen del token JWT y se cachean localmente.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();

  private rolesSubject = new BehaviorSubject<string[]>([]);
  public roles$ = this.rolesSubject.asObservable();

  private _toastService: ToastService | null = null;

  constructor(private injector: Injector) {
    this.loadPermissionsFromToken();
  }

  private get toastService(): ToastService {
    // Lazy loading para evitar dependencias circulares
    if (!this._toastService) {
      this._toastService = this.injector.get(ToastService);
    }
    return this._toastService;
  }

  /**
   * Carga los permisos desde el token JWT almacenado
   */
  loadPermissionsFromToken(): void {
    const token = localStorage.getItem('finance_access_token');
    if (!token) {
      this.permissionsSubject.next([]);
      this.rolesSubject.next([]);
      return;
    }

    try {
      const payload = this.decodeToken(token);
      
      // Extraer permisos (puede ser string o array)
      let permissions: string[] = [];
      if (payload.permission) {
        permissions = Array.isArray(payload.permission) 
          ? payload.permission 
          : [payload.permission];
      }

      // Extraer roles (buscar en múltiples claves posibles)
      let roles: string[] = [];
      const roleKeys = [
        'role',
        'roles',
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
      ];
      
      for (const key of roleKeys) {
        if (payload[key]) {
          roles = Array.isArray(payload[key]) 
            ? payload[key] 
            : [payload[key]];
          break;
        }
      }

      this.permissionsSubject.next(permissions);
      this.rolesSubject.next(roles);
    } catch (error) {
      console.error('Error al decodificar token:', error);
      this.permissionsSubject.next([]);
      this.rolesSubject.next([]);
    }
  }

  /**
   * Decodifica un token JWT
   */
  private decodeToken(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Token JWT inválido');
    }
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  hasPermission(permission: string): boolean {
    const permissions = this.permissionsSubject.value;
    return permissions.includes(permission);
  }

  /**
   * Verifica si el usuario tiene alguno de los permisos especificados
   */
  hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.permissionsSubject.value;
    return permissions.some(p => userPermissions.includes(p));
  }

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   */
  hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.permissionsSubject.value;
    return permissions.every(p => userPermissions.includes(p));
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: string): boolean {
    const roles = this.rolesSubject.value;
    return roles.includes(role);
  }

  /**
   * Verifica si el usuario es Super Administrador
   */
  isSuperAdmin(): boolean {
    return this.hasRole('SuperAdmin');
  }

  /**
   * Verifica si el usuario es Administrador (o superior)
   */
  isAdmin(): boolean {
    return this.hasRole('SuperAdmin') || this.hasRole('Admin');
  }

  /**
   * Verifica permiso y muestra toast si no tiene acceso
   */
  checkPermissionWithFeedback(permission: string, message?: string): boolean {
    if (this.hasPermission(permission)) {
      return true;
    }
    
    this.toastService.showToast(
      'warning', 
      message || 'No tienes permisos para realizar esta acción'
    );
    return false;
  }

  /**
   * Verifica alguno de los permisos y muestra toast si no tiene acceso
   */
  checkAnyPermissionWithFeedback(permissions: string[], message?: string): boolean {
    if (this.hasAnyPermission(permissions)) {
      return true;
    }
    
    this.toastService.showToast(
      'warning', 
      message || 'No tienes permisos para realizar esta acción'
    );
    return false;
  }

  /**
   * Obtiene los permisos del usuario para un módulo específico
   */
  getModulePermissions(module: string): string[] {
    const allPermissions = this.permissionsSubject.value;
    return allPermissions.filter(p => p.startsWith(`${module}_`));
  }

  /**
   * Verifica permisos CRUD para un módulo
   */
  canCreate(module: string): boolean {
    return this.hasPermission(`${module}_CREATE`);
  }

  canRead(module: string): boolean {
    return this.hasPermission(`${module}_READ`);
  }

  canUpdate(module: string): boolean {
    return this.hasPermission(`${module}_UPDATE`);
  }

  canDelete(module: string): boolean {
    return this.hasPermission(`${module}_DELETE`);
  }

  /**
   * Limpia los permisos (para logout)
   */
  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.rolesSubject.next([]);
  }

  /**
   * Recarga los permisos desde el token
   */
  refreshPermissions(): void {
    this.loadPermissionsFromToken();
  }

  /**
   * Obtiene todos los permisos actuales
   */
  getAllPermissions(): string[] {
    return this.permissionsSubject.value;
  }

  /**
   * Obtiene todos los roles actuales
   */
  getAllRoles(): string[] {
    return this.rolesSubject.value;
  }
}

